import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetAtom } from 'jotai';
import ConfirmDialog from '../common/components/ConfirmDialog';
import { showChannelMembersAtom } from './state';
import ToggleNotificationChannel from './ToggleNotificationChannel';
import useLeaveChannel from './hooks/useLeaveChannel';

export interface ChannelActionsProps {
  channel: GroupChannel;
  companyName: string;
}

export default function ChannelActions({ channel, companyName }: ChannelActionsProps): React.JSX.Element {
  const theme = useTheme()
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useTranslation([]);
  const setShowChannelMembers = useSetAtom(showChannelMembersAtom);
  const { leaveChannel } = useLeaveChannel();
  const [notificationAnchorEl, setNotificationsAnchorEl] = useState<HTMLElement | null>(null);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>): void => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = (): void => {
    setNotificationsAnchorEl(null);
  };

  const handleShowChannelMembersClick = (): void => {
    setShowChannelMembers(true);
    setActionsAnchorEl(null);
  };

  const handleLeaveChannelClick = (): void => {
    setIsLeaveConfirmOpen(true);
  };

  const handleLeaveConfirm = async (confirm: boolean): Promise<void> => {
    if (confirm) {
      await leaveChannel();
    }

    setIsLeaveConfirmOpen(false);
  };

  return (
    <>
      {isDownLg
        ? (
          <>
            <Button
              variant="text"
              onClick={(event) => setActionsAnchorEl(event.currentTarget)}
              sx={{ mt: -0.5 }}
            >
              <MoreVertIcon />
            </Button>
            <Menu
              open={!!actionsAnchorEl}
              anchorEl={actionsAnchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              onClose={() => setActionsAnchorEl(null)}
            >
              <MenuItem onClick={handleNotificationClick}>
                <ListItemIcon>
                  {
                    channel?.myPushTriggerOption === 'off'
                      ? <NotificationsOffOutlinedIcon />
                      : <NotificationsOutlinedIcon />
                  }
                </ListItemIcon>
                <ListItemText>
                  {
                    channel?.myPushTriggerOption === 'off'
                      ? t('chat:reactivate')
                      : t('chat:mute_conversation')
                  }
                </ListItemText>
              </MenuItem>
              <MenuItem onClick={handleShowChannelMembersClick}>
                <ListItemIcon>
                  <PeopleAltOutlinedIcon />
                </ListItemIcon>
                <ListItemText>
                  {t('chat:members_of_conversation')}
                </ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLeaveChannelClick}>
                <ListItemIcon>
                  <LogoutOutlinedIcon />
                </ListItemIcon>
                <ListItemText>
                  {t('chat:leave_conversation')}
                </ListItemText>
              </MenuItem>
            </Menu>
          </>
        )
        : (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant="text" onClick={handleNotificationClick}>
              {channel?.myPushTriggerOption === 'off'
                ? <NotificationsOffOutlinedIcon />
                : <NotificationsOutlinedIcon />}
            </Button>
            <Button variant="text" onClick={handleShowChannelMembersClick}>
              <PeopleAltOutlinedIcon />
            </Button>
            <Button variant="text" onClick={handleLeaveChannelClick}>
              <LogoutOutlinedIcon />
            </Button>
          </Box>
        )
      }
      <ToggleNotificationChannel anchorEl={notificationAnchorEl} onClose={handleNotificationClose} />
      <ConfirmDialog
        open={isLeaveConfirmOpen}
        onClose={handleLeaveConfirm}
        cancelLabel={t('common:cancel')}
        confirmLabel={t('common:quit')}
        confirmIcon={<LogoutOutlinedIcon />}
        title={t('chat:leave_conversation')}
        subTitle={t('chat:leave_conversation_description', { company: companyName })}
      />      
    </> 
  );
}
